import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import BlobField from '../components/BlobField';
import LogoMark from '../components/LogoMark';
import PasswordInput from '../components/PasswordInput';
import TurnstileWidget from '../components/TurnstileWidget';
import { useAuth } from '../context/AuthContext';
import { formatApiErrorDetail } from '../api';

/**
 * Sign Up — new screen (Claude didn't create one, but we need email/password since
 * you asked for real auth). Uses the exact glass/gradient design language from
 * the Sign In and Onboarding screens.
 *
 * Requires an already-validated invite code (passed via location.state.code
 * from the Onboarding screen).
 */
export default function SignUp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation();
  const code = location.state?.code;
  const referrer = location.state?.referrer || null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Phase 3 P0 anti-bot — Cloudflare Turnstile token. `null` until the
  // widget completes; then a single-use token. Backend re-verifies via
  // /siteverify before creating the account.
  const [turnstileToken, setTurnstileToken] = useState(null);

  // If someone navigates here directly without a code, bounce to onboarding.
  if (!code) {
    return <Navigate to="/onboarding" replace />;
  }

  const passwordOk = password.length >= 8;
  const matchOk = confirm.length > 0 && password === confirm;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = emailOk && passwordOk && matchOk && !busy && !!turnstileToken;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr('');
    try {
      await register({ code, email, password, turnstile_token: turnstileToken });
      navigate('/profile-setup', { replace: true });
    } catch (ex) {
      const detail = formatApiErrorDetail(ex.response?.data?.detail) || '';
      // Backend returns the English DISPOSABLE_EMAIL_MESSAGE from email_blocklist.py
      // when a throwaway domain is rejected. Detect it and swap in the localized copy.
      const isDisposable = /throwaway|permanent email address/i.test(detail);
      setErr(isDisposable ? t('auth.signUpDisposableEmail') : (detail || t('auth.signUpFallbackErr')));
      // Turnstile tokens are single-use — force a fresh token on retry.
      setTurnstileToken(null);
      setBusy(false);
    }
  };

  return (
    <>
      <BlobField />
      <div className="stage">
        <form className="card" onSubmit={submit} data-testid="signup-form">
          <LogoMark size={56} />
          <h1 className="heading">{t('auth.signUpHeadingPrefix')} <span className="grad">{t('auth.signUpBrand')}</span></h1>
          <p className="subcopy">
            <Trans
              i18nKey="auth.signUpCodeAccepted"
              values={{ code }}
              components={{ 1: <b style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--accent)' }} /> }}
            />
          </p>

          {referrer && (
            <div className="invited-chip" data-testid="invited-by-chip">
              <span className="invited-avatar" aria-hidden="true">
                {referrer.display_name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
              </span>
              <span className="invited-label">{t('auth.signUpInvitedBy')}</span>
              <span className="invited-handle">@{referrer.handle}</span>
            </div>
          )}

          <div className="field">
            <label className="field-label"><span>{t('common.email')}</span></label>
            <input
              type="email"
              className="field-input"
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              data-testid="signup-email-input"
            />
          </div>

          <div className="field">
            <label className="field-label"><span>{t('common.password')}</span><span className="char-count">{t('auth.signUpMin8')}</span></label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              testid="signup-password-input"
              toggleTestid="signup-password-toggle"
            />
          </div>

          <div className="field">
            <label className="field-label"><span>{t('auth.confirmPassword')}</span></label>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              testid="signup-confirm-input"
              toggleTestid="signup-confirm-toggle"
            />
            {confirm.length > 0 && !matchOk && (
              <div className="validation-row error" data-testid="signup-mismatch">{t('auth.signUpMismatch')}</div>
            )}
          </div>

          {err && (
            <p style={{ color: 'var(--error)', fontSize: 13, margin: '4px 0 12px', fontWeight: 700 }} data-testid="signup-error">
              {err}
            </p>
          )}

          <TurnstileWidget
            onToken={setTurnstileToken}
            onError={(msg) => setErr(msg)}
            action="signup"
          />

          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
            data-testid="signup-submit-btn"
          >
            {busy ? t('auth.signUpBusy') : t('auth.signUpCta')}
          </button>

          <div className="divider-row"><div className="line" /><span>{t('onboarding.orDivider')}</span><div className="line" /></div>

          <button
            type="button"
            className="btn-google"
            disabled
            data-testid="signup-google-btn"
            title="Coming at main launch"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
              <path d="M19.6 10.23c0-.68-.06-1.34-.17-1.97H10v3.73h5.4a4.6 4.6 0 0 1-2 3.02v2.5h3.23c1.9-1.75 2.97-4.33 2.97-7.28z" fill="#4285F4" />
              <path d="M10 20c2.7 0 4.97-.9 6.63-2.44l-3.24-2.5c-.9.6-2.04.96-3.39.96-2.61 0-4.82-1.76-5.6-4.13H1.05v2.6A10 10 0 0 0 10 20z" fill="#34A853" />
              <path d="M4.4 11.89A6.01 6.01 0 0 1 4.08 10c0-.66.11-1.3.32-1.9V5.51H1.05a10 10 0 0 0 0 8.98l3.35-2.6z" fill="#FBBC04" />
              <path d="M10 3.96c1.47 0 2.79.5 3.83 1.5l2.87-2.87A9.98 9.98 0 0 0 10 0 10 10 0 0 0 1.05 5.51l3.35 2.6C5.18 5.72 7.39 3.96 10 3.96z" fill="#EA4335" />
            </svg>
            {t('auth.continueGoogle')}
          </button>
          <p className="btn-google-note">{t('auth.googleNote')}</p>

          <p className="signup-note" style={{ marginTop: 14 }}>
            {t('auth.signUpAlreadyHave')} <Link to="/login" data-testid="signup-signin-link">{t('common.signIn')}</Link>
          </p>
        </form>
      </div>
    </>
  );
}
